import { FC, useMemo } from "react";
import clockIcon from "../../../../../../assets/clock.svg";
import coinIcon from "../../../../../../assets/coin.svg";
import "./ProposalsItemBasicInfo.scss";
import clubAvatar from "../../../../../../assets/clubAvatar.png";
import { IProposalWithMetaData } from "../../../../../../common/interfaces/proposals.interface";
import { ProposalState } from "@solana/spl-governance";
import { ProposalAction } from "../../../../../../common/enums/proposals.enums";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { IClubDataForProposal } from "../../../../../../common/interfaces/clubs.interface";
import {
  EMPTY_STRING,
  localeStringOptions,
  SOL_SYMBOL,
} from "../../../../../../common/constants/common.constants";
import { openNftOnMagicEden } from "../../../../../../solana/common/helpers/magic-eden.helpers";
import { getMintDecimalsDivider } from "../../../../../../utilities/helpers";

interface IProposalBasicInfo {
  proposalWithMetaData: IProposalWithMetaData | undefined;
  voteTimeToEnd;
  clubData: IClubDataForProposal;
}

const ProposalsItemBasicInfo: FC<IProposalBasicInfo> = ({
  proposalWithMetaData,
  voteTimeToEnd,
  clubData,
}) => {
  const showProposalImg = useMemo(() => {
    return (
      proposalWithMetaData?.metadata.proposalAction !==
        ProposalAction.Withdrawal &&
      proposalWithMetaData?.metadata.proposalAction !==
        ProposalAction.Discussion &&
      proposalWithMetaData?.metadata.proposalAction !==
        ProposalAction.UpdateGovernanceConfig &&
      proposalWithMetaData?.metadata.proposalAction !==
        ProposalAction.UpdateRoleConfig &&
      proposalWithMetaData?.metadata.proposalAction !== ProposalAction.Transfer
    );
  }, [proposalWithMetaData]);

  const buyNftProposalAction =
    proposalWithMetaData &&
    proposalWithMetaData.metadata.proposalAction === ProposalAction.BuyNft;
  const sellNftProposalAction =
    proposalWithMetaData &&
    proposalWithMetaData.metadata.proposalAction === ProposalAction.SellNft;

  const decimalsAmount = useMemo(() => {
    return proposalWithMetaData?.metadata.mintDecimals
      ? getMintDecimalsDivider(proposalWithMetaData?.metadata.mintDecimals)
      : LAMPORTS_PER_SOL;
  }, [proposalWithMetaData]);

  const offeredAmount = proposalWithMetaData
    ? proposalWithMetaData.metadata.offeredAmount / decimalsAmount
    : 0;
  const wantedAmount = proposalWithMetaData
    ? proposalWithMetaData.metadata.wantedAmount / decimalsAmount
    : 0;

  const getNftLinkOnMagicEden = useMemo(() => {
    if (!proposalWithMetaData?.metadata.isMagicEdenProposal)
      return EMPTY_STRING;
    return proposalWithMetaData?.metadata.proposalAction ===
      ProposalAction.SellNft
      ? proposalWithMetaData?.metadata.offeredTokenMint.toString()
      : proposalWithMetaData?.metadata.wantedTokenMint.toString();
  }, [proposalWithMetaData]);

  return (
    <div className="proposals-item-basic-info">
      {showProposalImg && (
        <img
          onClick={
            proposalWithMetaData?.metadata.isMagicEdenProposal
              ? () => openNftOnMagicEden(getNftLinkOnMagicEden)
              : () => {}
          }
          src={
            proposalWithMetaData?.metadata.nftImgUrl &&
            proposalWithMetaData!.metadata.nftImgUrl !== EMPTY_STRING
              ? proposalWithMetaData!.metadata.nftImgUrl
              : clubAvatar
          }
          alt="proposal_image"
          className="proposals-item-basic-info__image"
        />
      )}
      <div className="proposals-item-basic-info__text">
        <p className="proposals-item-basic-info__name">
          {proposalWithMetaData!.proposalBasicData.proposalAccount.account.name}
        </p>
        <div className="proposals-item-basic-info__info">
          {clubData.isDashboardProposal && (
            <div className="proposals-item-basic-info__club-data">
              <img
                src={
                  clubData.clubImageUrl &&
                  clubData.clubImageUrl !== EMPTY_STRING
                    ? clubData.clubImageUrl
                    : clubAvatar
                }
                alt="clubImage"
              />
              <p>{clubData.clubName}</p>
            </div>
          )}
          <div className="proposals-item-basic-info__duration">
            <img src={clockIcon} alt="clockIcon" />
            {proposalWithMetaData?.proposalBasicData.proposalAccount.account
              .state !== ProposalState.Cancelled &&
            !proposalWithMetaData!.proposalBasicData.proposalAccount.account
              .votingCompletedAt ? (
              <p className="proposals-item-basic-info__ends-in">
                Voting ends in{" "}
                <span>
                  {voteTimeToEnd
                    ? `${voteTimeToEnd.days}d ${voteTimeToEnd.hours}h ${voteTimeToEnd.minutes}m`
                    : ""}
                </span>
              </p>
            ) : (
              <p className="proposals-item-basic-info__ends-in">
                Voting has ended
              </p>
            )}
          </div>
          {(buyNftProposalAction || sellNftProposalAction) && (
            <div className="proposals-item-basic-info__trade">
              <img src={coinIcon} alt="coin" />
              <p className="proposals-item-basic-info__trade-option">
                {buyNftProposalAction ? "Buy for" : "Sell for"}
                <span className="proposals-item-basic-info__trade-amount">
                  {buyNftProposalAction
                    ? offeredAmount.toLocaleString(
                        undefined,
                        localeStringOptions
                      )
                    : wantedAmount.toLocaleString(
                        undefined,
                        localeStringOptions
                      )}{" "}
                  {proposalWithMetaData?.metadata.mintSymbol ?? SOL_SYMBOL}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalsItemBasicInfo;
